import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import qs from 'qs'

import {
  AppContextMenu,
  AppDivider,
  AppSegment,
  AppFlexContainer,
  AppFlexItem,
  AppLoader,
} from 'client/ui-building-blocks'

import { removeBlob, getUserAvatarUrl, displaySnackBar } from 'client/services'
import { GenericError, UserAvatar } from 'client/components'
import { useGetUser } from 'client/api'
import { usePage, useAppContext, useEnvContext, usePageStateContext } from 'client/hooks'

import Badge from './Badge/badge'
import Stats from './Stats/stats'

const ProfilePage = () => {
  const { t } = useTranslation()
  const { pathname, search } = useLocation()

  const { navigateTo } = usePage()
  const { awsBucketUrl } = useEnvContext()
  const { authUser } = useAppContext()
  const { setSettingsPageState } = usePageStateContext()

  const { data: profilePageData, refetch, isError, isLoading, isFetching } = useGetUser()

  useLayoutEffect(() => {
    if (pathname === '/profile') {
      const { _id } = qs.parse(search.substring(1))
      if (_id) {
        removeBlob(getUserAvatarUrl(awsBucketUrl, _id.toString()))
      }
      refetch().then(res => {
        if (res.isError && res.error?.msg) {
          displaySnackBar(res.error.msg)
        }
      })
    }
  }, [search])

  if (!authUser) return null

  if (isError) {
    return <GenericError />
  }

  const contextMenuItems = []

  if (authUser.config.confirmed) {
    contextMenuItems.push({
      label: t('profilePage.contextMenu.changeAvatar'),
      onClick: () => {
        setSettingsPageState({ tabNo: 4 })
        navigateTo({ path: '/settings' })
      },
    })
  }

  contextMenuItems.push({
    label: t('profilePage.contextMenu.updatePersonalDetails'),
    onClick: () => {
      setSettingsPageState({ tabNo: 2 })
      navigateTo({ path: '/settings' })
    },
  })

  const isProfileUserAuthUser =
    authUser._id.toString() === profilePageData?.data.user._id.toString()

  return (
    <AppSegment
      headerProps={{
        text: t('profilePage.header'),
        iconProps: {
          name: 'USER',
        },
        rightSlot: isProfileUserAuthUser ? (
          <AppContextMenu items={contextMenuItems} pointing="top right" />
        ) : null,
      }}
    >
      <AppLoader active={isLoading || isFetching}>
        {!isError && (
          <AppFlexContainer
            flexFlow="column"
            justifyContent="space-evenly"
            alignItems="center"
            columnGap="50px"
          >
            <AppFlexItem>
              <AppFlexContainer justifyContent="space-between" columnGap="150px">
                <AppFlexItem>
                  <Badge data={profilePageData?.data} />
                </AppFlexItem>
                <AppFlexItem flexGrow={1}>
                  <UserAvatar
                    userId={profilePageData?.data?.user._id.toString()}
                    size="large"
                  />
                </AppFlexItem>
              </AppFlexContainer>
            </AppFlexItem>
            <AppFlexItem flexGrow={1}>
              <Stats
                data={profilePageData}
                isProfileUserAuthUser={isProfileUserAuthUser}
              />
            </AppFlexItem>
          </AppFlexContainer>
        )}
      </AppLoader>
      <AppDivider size="large" />
    </AppSegment>
  )
}

export default ProfilePage
